import { Box, Button, Heading, ListItem, Text, UnorderedList } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

// id is optional for backward compatibility: callers that don't have a stable id
// fall back to name+index as a key (acceptable for short lists with stable names).
interface Entry {
  id?: string;
  name: string;
  reason?: string | null;
}

interface Props {
  invited: { id?: string; name: string }[];
  notSelected: Entry[];
  rejected: Entry[];
  collapsedOver?: number;
}

interface GroupProps {
  title: string;
  description: string;
  entries: Entry[];
  showReasons: boolean;
  collapsedOver: number;
}

const RecipientGroup = ({ title, description, entries, showReasons, collapsedOver }: GroupProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const visible = expanded || entries.length <= collapsedOver ? entries : entries.slice(0, collapsedOver);
  const hiddenCount = entries.length - collapsedOver;

  return (
    <Box mt="4">
      <Heading as="h4" size="xs" color="smText">
        {title} ({entries.length})
      </Heading>
      <Text fontSize="xs" color="smMuted" mt="0.5">
        {description}
      </Text>
      <UnorderedList mt="1.5" spacing="0.5">
        {visible.map((entry, idx) => (
          <ListItem key={entry.id ?? `${entry.name}-${idx}`} fontSize="sm" color="smText">
            {entry.name}
            {showReasons && entry.reason && (
              <Text as="span" color="smMuted" fontStyle="italic">
                {` — ${entry.reason}`}
              </Text>
            )}
          </ListItem>
        ))}
      </UnorderedList>
      {!expanded && hiddenCount > 0 && (
        <Button
          size="xs"
          variant="link"
          colorScheme="teal"
          mt="1"
          onClick={() => setExpanded(true)}
        >
          {t("+ {{count}} more — show all", { count: hiddenCount })}
        </Button>
      )}
    </Box>
  );
};

export const RecipientGroups = ({ invited, notSelected, rejected, collapsedOver = 5 }: Props) => {
  const { t } = useTranslation();

  return (
    <>
      {invited.length > 0 && (
        <RecipientGroup
          title={t("Invited")}
          description={t("Receive the invitation to tender with the bid deadline")}
          entries={invited}
          showReasons={false}
          collapsedOver={collapsedOver}
        />
      )}
      {notSelected.length > 0 && (
        <RecipientGroup
          title={t("Qualified, but not selected")}
          description={t("Informed in writing with your reason (FOA § 16-12)")}
          entries={notSelected}
          showReasons={true}
          collapsedOver={collapsedOver}
        />
      )}
      {rejected.length > 0 && (
        <RecipientGroup
          title={t("Not qualified")}
          description={t("Informed that their request was rejected")}
          entries={rejected}
          showReasons={true}
          collapsedOver={collapsedOver}
        />
      )}
    </>
  );
};
